import React, { useState, useEffect, useMemo } from 'react';
import {
  Key, Search, Shield, CheckCircle, XCircle, Clock, Database, AlertTriangle,
  RefreshCw, Download, Eye, EyeOff, Copy, Filter, Trash2, Play, Globe,
  Mail, Lock, Hash, Calendar, Server, ExternalLink, MoreVertical, FileText,
  TrendingUp, Users, Zap, ChevronDown, ChevronUp, Layers, BookOpen, User,
  Phone, MapPin, Building, Briefcase, Heart, X, BadgeCheck, Loader2, Save,
  FolderOpen, Library, Crown
} from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useI18n } from '../../i18n';
import CGUModal from '../../components/CGUModal';
import { toast } from '../../utils/toastUtils';
import { credentials as credentialsApi, workspaces as workspacesApi } from '../../services/api/client';
import { CredentialsWelcomeBanner } from '../../components/WelcomeBanner';
import PlanRestrictedButton from '../../components/PlanRestrictedButton';
import { usePlanRestrictions } from '../../hooks/usePlanRestrictions';
import LicenseBlock, { LicenseGatedButton } from '../../components/LicenseBlock';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import OSINTBreachChecker from '../../components/OSINTBreachChecker';
import OSINTEnrichmentPanel from '../../components/OSINTEnrichmentPanel';

// ============================================
// DICTIONARY GENERATOR MODAL (CUPP-like) - Enhanced with auto-extraction
// ============================================
const DictionaryGeneratorModal = ({ isOpen, onClose, credentials, selectedEmail }) => {
  const { t } = useI18n();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    birthDate: '',
    partnerName: '',
    partnerBirthDate: '',
    childName: '',
    petName: '',
    company: '',
    keywords: '',
    phone: '',
    city: '',
    // New fields from credentials
    knownPasswords: [],
    ipAddresses: [],
    usernames: [],
    domains: [],
    // Options de generation
    addNumbers: true,
    addSpecialChars: true,
    addYears: true,
    leetSpeak: true,
    addCommonPatterns: true,
    useKnownPasswords: true,
    generateVariations: true,
    minLength: 6,
    maxLength: 20
  });

  const [generatedWords, setGeneratedWords] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dictionaryName, setDictionaryName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [maxWords, setMaxWords] = useState(1000);  // Default limit
  const [savedDictionaries, setSavedDictionaries] = useState([]);
  const [showDictionariesList, setShowDictionariesList] = useState(false);
  const [loadingDictionaries, setLoadingDictionaries] = useState(false);

  // Workspace selection for saving dictionary
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

  // Use all credentials mode
  const [useAllCredentials, setUseAllCredentials] = useState(false);

  // Export format for bruteforce tools
  const [exportFormat, setExportFormat] = useState('wordlist'); // wordlist, hydra, medusa, hashcat_rules

  // Word count options
  const WORD_COUNT_OPTIONS = [
    { value: 100, label: '100 mots', description: 'Test rapide' },
    { value: 500, label: '500 mots', description: 'Standard' },
    { value: 1000, label: '1 000 mots', description: 'Recommandé' },
    { value: 5000, label: '5 000 mots', description: 'Exhaustif' },
    { value: 10000, label: '10 000 mots', description: 'Maximum' }
  ];

  // Export format options for bruteforce tools
  const EXPORT_FORMATS = [
    { value: 'wordlist', label: 'Wordlist Standard', description: 'Un mot par ligne (compatible tous outils)', icon: '📄' },
    { value: 'hydra', label: 'Hydra Format', description: 'Format user:password pour Hydra', icon: '🔐' },
    { value: 'medusa', label: 'Medusa Format', description: 'Format host:user:password pour Medusa', icon: '🐍' },
    { value: 'combo', label: 'Combo List', description: 'email:password pour credential stuffing', icon: '📋' },
    { value: 'hashcat_rules', label: 'Hashcat Rules', description: 'Base words pour hashcat avec règles', icon: '⚡' },
  ];

  // Load workspaces on modal open
  useEffect(() => {
    const fetchWorkspaces = async () => {
      if (!isOpen) return;
      setLoadingWorkspaces(true);
      try {
        const response = await workspacesApi.list(0, 100);
        setWorkspaces(Array.isArray(response) ? response : (response.workspaces || []));
      } catch (error) {
        console.error('Failed to fetch workspaces:', error);
        setWorkspaces([]);
      } finally {
        setLoadingWorkspaces(false);
      }
    };
    fetchWorkspaces();
  }, [isOpen]);

  // Enhanced extraction - Pre-fill from ALL credential data for selected email
  useEffect(() => {
    if (selectedEmail && credentials) {
      // Find ALL credentials for this email to extract maximum data
      const matchingCreds = credentials.filter(c =>
        c.email === selectedEmail ||
        c.username === selectedEmail ||
        (c.email && c.email.toLowerCase() === selectedEmail.toLowerCase())
      );

      if (matchingCreds.length > 0) {
        // Extract info from email
        const emailParts = selectedEmail.split('@');
        const localPart = emailParts[0] || '';
        const domain = emailParts[1] || '';

        // Try to extract name from email using various patterns
        let firstName = '', lastName = '';

        // Pattern: firstname.lastname@domain
        if (localPart.includes('.')) {
          const parts = localPart.split('.');
          firstName = parts[0] || '';
          lastName = parts[1] || '';
        }
        // Pattern: firstname_lastname@domain
        else if (localPart.includes('_')) {
          const parts = localPart.split('_');
          firstName = parts[0] || '';
          lastName = parts[1] || '';
        }
        // Pattern: firstnamelastname@domain (try to split by capital)
        else {
          const nameParts = localPart.replace(/[0-9]/g, '').split(/(?=[A-Z])/).filter(Boolean);
          if (nameParts.length >= 2) {
            firstName = nameParts[0] || '';
            lastName = nameParts.slice(1).join('') || '';
          } else {
            firstName = localPart.replace(/[0-9]/g, '') || '';
          }
        }

        // Collect ALL known passwords (for variations)
        const knownPasswords = [];
        const phones = [];
        const ips = [];
        const usernames = new Set();
        const allDomains = new Set();
        const names = new Set();
        const cities = new Set();

        matchingCreds.forEach(cred => {
          // Collect passwords
          if (cred.password) {
            knownPasswords.push(cred.password);
          }

          // Collect phones
          if (cred.phone) {
            phones.push(cred.phone);
          }

          // Collect IPs
          if (cred.ip_address) {
            ips.push(cred.ip_address);
          }

          // Collect usernames
          if (cred.username && cred.username !== cred.email) {
            usernames.add(cred.username);
          }

          // Collect names from 'name' field if present
          if (cred.name) {
            names.add(cred.name);
          }

          // Collect domains
          if (cred.domain) {
            allDomains.add(cred.domain);
          }
          if (cred.email) {
            const d = cred.email.split('@')[1];
            if (d) allDomains.add(d.split('.')[0]);
          }
        });

        // Also scan ALL credentials for same domain to find more patterns
        const domainPattern = domain ? domain.split('.')[0].toLowerCase() : '';
        if (domainPattern) {
          credentials.forEach(cred => {
            const credDomain = (cred.email || '').split('@')[1] || '';
            if (credDomain.toLowerCase().includes(domainPattern)) {
              if (cred.password) knownPasswords.push(cred.password);
              if (cred.phone) phones.push(cred.phone);
              if (cred.name) names.add(cred.name);
            }
          });
        }

        // Extract additional keywords from names
        const extractedNames = Array.from(names);
        extractedNames.forEach(name => {
          const parts = name.split(/\s+/);
          if (parts.length >= 2) {
            if (!firstName) firstName = parts[0];
            if (!lastName) lastName = parts[parts.length - 1];
          }
        });

        // Extract numbers from email that might be birth year
        const yearMatch = localPart.match(/(\d{4})/);
        const shortYearMatch = localPart.match(/(\d{2})$/);
        let birthYear = '';
        if (yearMatch && parseInt(yearMatch[1]) >= 1950 && parseInt(yearMatch[1]) <= 2010) {
          birthYear = yearMatch[1];
        } else if (shortYearMatch) {
          const year = parseInt(shortYearMatch[1]);
          if (year >= 50 && year <= 99) birthYear = `19${shortYearMatch[1]}`;
          else if (year >= 0 && year <= 24) birthYear = `20${shortYearMatch[1].padStart(2, '0')}`;
        }

        // Build keywords from all collected data
        const keywordSet = new Set();
        keywordSet.add(localPart);
        keywordSet.add(domain ? domain.split('.')[0] : '');
        Array.from(usernames).forEach(u => keywordSet.add(u));
        Array.from(allDomains).forEach(d => keywordSet.add(d));

        // Clean up firstName/lastName
        firstName = firstName.replace(/[0-9]/g, '').toLowerCase();
        lastName = lastName.replace(/[0-9]/g, '').toLowerCase();

        const extracted = {
          totalCredentials: matchingCreds.length,
          knownPasswords: [...new Set(knownPasswords)].slice(0, 20),
          phones: [...new Set(phones)],
          ips: [...new Set(ips)],
          usernames: Array.from(usernames),
          names: extractedNames,
          domains: Array.from(allDomains)
        };

        setExtractedData(extracted);

        setProfile(prev => ({
          ...prev,
          firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1),
          lastName: lastName.charAt(0).toUpperCase() + lastName.slice(1),
          nickname: Array.from(usernames)[0] || '',
          birthDate: birthYear ? `01/01/${birthYear}` : '',
          company: domain ? domain.split('.')[0] : '',
          phone: phones[0] || '',
          keywords: Array.from(keywordSet).filter(Boolean).join(', '),
          knownPasswords: extracted.knownPasswords,
          ipAddresses: extracted.ips,
          usernames: extracted.usernames,
          domains: extracted.domains
        }));
      }
    }
  }, [selectedEmail, credentials]);

  // CUPP-like password generation algorithm - Enhanced with known passwords
  const generateDictionary = () => {
    setGenerating(true);
    const words = new Set();

    // Base words from profile
    const baseWords = [];
    if (profile.firstName) baseWords.push(profile.firstName.toLowerCase());
    if (profile.lastName) baseWords.push(profile.lastName.toLowerCase());
    if (profile.nickname) baseWords.push(profile.nickname.toLowerCase());
    if (profile.partnerName) baseWords.push(profile.partnerName.toLowerCase());
    if (profile.childName) baseWords.push(profile.childName.toLowerCase());
    if (profile.petName) baseWords.push(profile.petName.toLowerCase());
    if (profile.company) baseWords.push(profile.company.toLowerCase());
    if (profile.city) baseWords.push(profile.city.toLowerCase());

    // Add usernames as base words
    if (profile.usernames && profile.usernames.length > 0) {
      profile.usernames.forEach(u => {
        if (u) baseWords.push(u.toLowerCase());
      });
    }

    // Add keywords
    if (profile.keywords) {
      profile.keywords.split(',').forEach(kw => {
        const clean = kw.trim().toLowerCase();
        if (clean && clean.length > 2) baseWords.push(clean);
      });
    }

    // Extract years from dates
    const years = [];
    if (profile.birthDate) {
      const parts = profile.birthDate.split(/[-/]/);
      parts.forEach(p => {
        if (p.length === 4) years.push(p);
        if (p.length === 2) years.push(p);
      });
    }
    if (profile.partnerBirthDate) {
      const parts = profile.partnerBirthDate.split(/[-/]/);
      parts.forEach(p => {
        if (p.length === 4) years.push(p);
        if (p.length === 2) years.push(p);
      });
    }

    // Extract numbers from phone
    if (profile.phone) {
      const phoneDigits = profile.phone.replace(/\D/g, '');
      if (phoneDigits.length >= 4) {
        years.push(phoneDigits.slice(-4));
        years.push(phoneDigits.slice(-2));
        years.push(phoneDigits.slice(0, 4));
      }
    }

    // Common patterns
    const commonNumbers = ['1', '12', '123', '1234', '12345', '01', '69', '666', '777', '2024', '2023', '2022', '2021', '2020', '!', '@', '#'];
    const specialChars = ['!', '@', '#', '$', '%', '&', '*', '.', '_', '-'];

    // Leet speak mapping
    const leetMap = { 'a': '4', 'e': '3', 'i': '1', 'o': '0', 's': '5', 't': '7', 'l': '1', 'b': '8' };
    const toLeet = (str) => str.split('').map(c => leetMap[c.toLowerCase()] || c).join('');

    // Capitalize variations helper
    const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

    // ============================================
    // PHASE 1: Use known passwords and variations
    // ============================================
    if (profile.useKnownPasswords && profile.knownPasswords && profile.knownPasswords.length > 0) {
      profile.knownPasswords.forEach(pwd => {
        if (!pwd) return;

        // Add original
        words.add(pwd);

        // Common variations of known passwords
        if (profile.generateVariations) {
          // Increment/decrement numbers at end
          const numMatch = pwd.match(/^(.+?)(\d+)$/);
          if (numMatch) {
            const base = numMatch[1];
            const num = parseInt(numMatch[2]);
            for (let i = -5; i <= 5; i++) {
              if (num + i > 0) {
                words.add(base + (num + i));
              }
            }
          }

          // Change year at end
          const yearMatch = pwd.match(/^(.+?)(20\d{2}|19\d{2})$/);
          if (yearMatch) {
            const base = yearMatch[1];
            for (let y = 2018; y <= 2025; y++) {
              words.add(base + y);
            }
          }

          // Add/remove special chars
          specialChars.forEach(char => {
            words.add(pwd + char);
            words.add(char + pwd);
            if (pwd.endsWith(char)) {
              words.add(pwd.slice(0, -1));
            }
          });

          // Capitalize variations
          words.add(pwd.toLowerCase());
          words.add(pwd.toUpperCase());
          words.add(capitalize(pwd.toLowerCase()));

          // Leet speak
          words.add(toLeet(pwd));

          // Reverse
          words.add(pwd.split('').reverse().join(''));
        }
      });
    }

    // ============================================
    // PHASE 2: Generate from base words
    // ============================================
    baseWords.forEach(word => {
      if (!word || word.length < 2) return;

      // Basic variations
      words.add(word);
      words.add(capitalize(word));
      words.add(word.toUpperCase());

      // With numbers
      if (profile.addNumbers) {
        commonNumbers.forEach(num => {
          words.add(word + num);
          words.add(capitalize(word) + num);
          words.add(num + word);
        });
        years.forEach(year => {
          words.add(word + year);
          words.add(capitalize(word) + year);
          words.add(year + word);
          words.add(word + year + '!');
          words.add(capitalize(word) + year + '!');
        });
      }

      // With special chars
      if (profile.addSpecialChars) {
        specialChars.forEach(char => {
          words.add(word + char);
          words.add(char + word);
          words.add(capitalize(word) + char);
          // Double special chars
          words.add(word + char + char);
        });
      }

      // Leet speak
      if (profile.leetSpeak) {
        const leetWord = toLeet(word);
        words.add(leetWord);
        words.add(capitalize(leetWord));
        if (profile.addNumbers) {
          commonNumbers.forEach(num => {
            words.add(leetWord + num);
          });
          years.forEach(year => {
            words.add(leetWord + year);
          });
        }
      }
    });

    // ============================================
    // PHASE 3: Combinations of two words
    // ============================================
    if (baseWords.length > 1) {
      for (let i = 0; i < Math.min(baseWords.length, 10); i++) {
        for (let j = 0; j < Math.min(baseWords.length, 10); j++) {
          if (i !== j && baseWords[i] && baseWords[j]) {
            const word1 = baseWords[i];
            const word2 = baseWords[j];
            const combo = word1 + word2;

            words.add(combo);
            words.add(capitalize(word1) + capitalize(word2));
            words.add(capitalize(word1) + word2);
            words.add(word1 + capitalize(word2));

            // With separator
            words.add(word1 + '_' + word2);
            words.add(word1 + '.' + word2);
            words.add(word1 + '-' + word2);

            // With numbers
            if (profile.addNumbers) {
              years.forEach(year => {
                words.add(combo + year);
                words.add(word1 + year + word2);
                words.add(capitalize(word1) + capitalize(word2) + year);
              });
              commonNumbers.slice(0, 5).forEach(num => {
                words.add(combo + num);
              });
            }
          }
        }
      }
    }

    // ============================================
    // PHASE 4: Common password patterns
    // ============================================
    if (profile.addCommonPatterns) {
      baseWords.forEach(word => {
        if (!word) return;

        // Classic patterns
        words.add(word + '123');
        words.add(word + '1234');
        words.add(word + '12345');
        words.add(word + '123456');
        words.add(word + '!');
        words.add(word + '!!');
        words.add(word + '@');
        words.add(word + '#');
        words.add(word + '$');
        words.add(word + '2024');
        words.add(word + '2024!');
        words.add(word + '2023');
        words.add(word + '2023!');
        words.add(word + '@2024');
        words.add(word + '@2023');

        // Capitalized patterns
        words.add(capitalize(word) + '123');
        words.add(capitalize(word) + '123!');
        words.add(capitalize(word) + '1234');
        words.add(capitalize(word) + '12345');
        words.add(capitalize(word) + '@2024');
        words.add(capitalize(word) + '@2023');
        words.add(capitalize(word) + '!');
        words.add(capitalize(word) + '!!');

        // Wrapped patterns
        words.add('!' + word + '!');
        words.add('@' + word + '@');
        words.add('*' + word + '*');
        words.add(word + '007');
        words.add(word + '666');
        words.add(word + '69');

        // French common patterns
        words.add(word + 'azerty');
        words.add('azerty' + word);
        words.add(word + 'soleil');
        words.add(word + 'amour');
      });

      // Add universal common passwords based on profile
      if (profile.firstName && profile.lastName) {
        const fn = profile.firstName.toLowerCase();
        const ln = profile.lastName.toLowerCase();
        words.add(fn + ln);
        words.add(ln + fn);
        words.add(fn + '.' + ln);
        words.add(fn + '_' + ln);
        words.add(fn[0] + ln);
        words.add(fn + ln[0]);
        years.forEach(year => {
          words.add(fn + ln + year);
          words.add(fn + year);
          words.add(ln + year);
        });
      }
    }

    // ============================================
    // PHASE 5: Filter by length and dedupe
    // ============================================
    const filtered = Array.from(words).filter(w =>
      w && w.length >= profile.minLength && w.length <= profile.maxLength
    );

    // Sort: known passwords first, then by length
    const knownSet = new Set(profile.knownPasswords || []);
    const sorted = filtered.sort((a, b) => {
      const aKnown = knownSet.has(a) ? 0 : 1;
      const bKnown = knownSet.has(b) ? 0 : 1;
      if (aKnown !== bKnown) return aKnown - bKnown;
      return a.length - b.length;
    });

    // Limit to maxWords
    const limited = sorted.slice(0, maxWords);

    setGeneratedWords(limited);
    setShowPreview(true);
    setGenerating(false);
    toast.success(`${limited.length} mots de passe generes!${sorted.length > maxWords ? ` (limite a ${maxWords})` : ''}`);
  };

  // Save dictionary to backend
  const saveDictionary = async () => {
    if (!dictionaryName.trim()) {
      toast.error('Veuillez entrer un nom pour le dictionnaire');
      return;
    }

    if (generatedWords.length === 0) {
      toast.error('Aucun mot de passe a sauvegarder');
      return;
    }

    try {
      setSaving(true);
      const result = await credentialsApi.saveDictionary({
        name: dictionaryName.trim(),
        description: `Dictionnaire généré pour ${selectedEmail || 'cible personnalisée'} - ${profile.firstName} ${profile.lastName}`.trim(),
        words: generatedWords,
        target_email: selectedEmail,
        workspace_id: selectedWorkspaceId || null,
        export_format: exportFormat,
        target_profile: {
          firstName: profile.firstName,
          lastName: profile.lastName,
          company: profile.company,
          city: profile.city
        }
      });

      toast.success(`Dictionnaire "${dictionaryName}" sauvegarde avec ${result.words_count} mots!`);
      setShowSaveModal(false);
      setDictionaryName('');
    } catch (error) {
      console.error('Error saving dictionary:', error);
      toast.error('Erreur lors de la sauvegarde du dictionnaire');
    } finally {
      setSaving(false);
    }
  };

  // Load saved dictionaries
  const loadDictionaries = async () => {
    try {
      setLoadingDictionaries(true);
      const result = await credentialsApi.listDictionaries();
      setSavedDictionaries(result.dictionaries || []);
      setShowDictionariesList(true);
    } catch (error) {
      console.error('Error loading dictionaries:', error);
      toast.error('Erreur lors du chargement des dictionnaires');
    } finally {
      setLoadingDictionaries(false);
    }
  };

  // Load a specific dictionary
  const loadDictionary = async (dictId) => {
    try {
      const result = await credentialsApi.getDictionary(dictId);
      setGeneratedWords(result.words || []);
      setShowPreview(true);
      setShowDictionariesList(false);
      toast.success(`Dictionnaire "${result.name}" charge avec ${result.words_count} mots`);
    } catch (error) {
      console.error('Error loading dictionary:', error);
      toast.error('Erreur lors du chargement du dictionnaire');
    }
  };

  // Delete a dictionary
  const deleteDictionary = async (dictId) => {
    if (!window.confirm(t('common.confirmDeleteItem'))) return;

    try {
      await credentialsApi.deleteDictionary(dictId);
      setSavedDictionaries(prev => prev.filter(d => d.id !== dictId));
      toast.success('Dictionnaire supprime');
    } catch (error) {
      console.error('Error deleting dictionary:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Export a saved dictionary directly
  const exportSavedDictionary = async (dictId, dictName) => {
    try {
      const result = await credentialsApi.getDictionary(dictId);
      const content = (result.words || []).join('\n');
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dictName.replace(/[^a-zA-Z0-9_-]/g, '_')}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Dictionnaire "${dictName}" telecharge!`);
    } catch (error) {
      console.error('Error exporting dictionary:', error);
      toast.error('Erreur lors du telechargement');
    }
  };

  // Format dictionary for different bruteforce tools
  const formatForExport = (words, format) => {
    // Get unique emails/usernames from credentials for combo formats
    const uniqueUsers = [...new Set(credentials.filter(c => c.email || c.username).map(c => c.email || c.username))];

    switch (format) {
      case 'hydra':
        // Format: user:password (one per line, cycling through users)
        const hydraLines = [];
        uniqueUsers.forEach(user => {
          words.forEach(word => {
            hydraLines.push(`${user}:${word}`);
          });
        });
        return hydraLines.join('\n');

      case 'medusa':
        // Format: host:user:password (host placeholder = TARGET)
        const medusaLines = [];
        uniqueUsers.forEach(user => {
          words.forEach(word => {
            medusaLines.push(`TARGET:${user}:${word}`);
          });
        });
        return medusaLines.join('\n');

      case 'combo':
        // Format: email:password (credential stuffing)
        const comboLines = [];
        uniqueUsers.filter(u => u.includes('@')).forEach(email => {
          words.forEach(word => {
            comboLines.push(`${email}:${word}`);
          });
        });
        return comboLines.join('\n');

      case 'hashcat_rules':
        // Just base words, meant to be used with hashcat rules
        // Filter to shorter base words (better for rule-based attacks)
        return words.filter(w => w.length <= 12 && !/[!@#$%^&*]/.test(w)).join('\n');

      default:
        // Standard wordlist
        return words.join('\n');
    }
  };

  const downloadDictionary = () => {
    const content = formatForExport(generatedWords, exportFormat);
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Filename based on format
    const formatSuffix = {
      'wordlist': 'wordlist',
      'hydra': 'hydra',
      'medusa': 'medusa',
      'combo': 'combo',
      'hashcat_rules': 'hashcat_base'
    };
    a.download = `dictionary_${selectedEmail || 'custom'}_${formatSuffix[exportFormat] || 'wordlist'}_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    const lineCount = content.split('\n').length;
    toast.success(`Dictionnaire ${exportFormat.toUpperCase()} telecharge! (${lineCount.toLocaleString()} lignes)`);
  };

  // Generate from ALL credentials (batch mode)
  const generateFromAllCredentials = () => {
    setGenerating(true);
    const words = new Set();

    // Collect ALL data from ALL credentials
    const allPasswords = [];
    const allEmails = [];
    const allUsernames = [];
    const allNames = [];
    const allPhones = [];

    credentials.forEach(cred => {
      if (cred.password) allPasswords.push(cred.password);
      if (cred.email) allEmails.push(cred.email);
      if (cred.username) allUsernames.push(cred.username);
      if (cred.name) allNames.push(cred.name);
      if (cred.phone) allPhones.push(cred.phone);
    });

    // Add all known passwords and variations
    allPasswords.forEach(pwd => {
      words.add(pwd);
      words.add(pwd.toLowerCase());
      words.add(pwd.toUpperCase());
      // Number variations
      const numMatch = pwd.match(/^(.+?)(\d+)$/);
      if (numMatch) {
        const base = numMatch[1];
        const num = parseInt(numMatch[2]);
        for (let i = -3; i <= 3; i++) {
          if (num + i > 0) words.add(base + (num + i));
        }
        // Year variations
        for (let y = 2020; y <= 2025; y++) {
          words.add(base + y);
        }
      }
      // Special char variations
      ['!', '@', '#', '123', '1'].forEach(suffix => {
        words.add(pwd + suffix);
      });
    });

    // Extract words from emails
    allEmails.forEach(email => {
      const localPart = email.split('@')[0];
      words.add(localPart);
      words.add(localPart.toLowerCase());
      // Split on common separators
      localPart.split(/[._-]/).forEach(part => {
        if (part.length > 2) {
          words.add(part);
          words.add(part.toLowerCase());
          words.add(part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
        }
      });
    });

    // Add usernames
    allUsernames.forEach(username => {
      words.add(username);
      words.add(username.toLowerCase());
      ['123', '1', '!', '@'].forEach(suffix => {
        words.add(username + suffix);
      });
    });

    // Extract from names
    allNames.forEach(name => {
      const parts = name.split(/\s+/);
      parts.forEach(part => {
        if (part.length > 2) {
          words.add(part.toLowerCase());
          words.add(part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
        }
      });
      // First + Last combos
      if (parts.length >= 2) {
        const first = parts[0].toLowerCase();
        const last = parts[parts.length - 1].toLowerCase();
        words.add(first + last);
        words.add(last + first);
        words.add(first + '.' + last);
        words.add(first[0] + last);
        ['123', '!', '2024', '2023'].forEach(suffix => {
          words.add(first + last + suffix);
        });
      }
    });

    // Phone number patterns
    allPhones.forEach(phone => {
      const digits = phone.replace(/\D/g, '');
      if (digits.length >= 4) {
        words.add(digits.slice(-4));
        words.add(digits.slice(-6));
        words.add(digits);
      }
    });

    // Filter by length
    const filtered = Array.from(words).filter(w =>
      w && w.length >= profile.minLength && w.length <= profile.maxLength
    );

    setGeneratedWords(filtered.slice(0, maxWords));
    setShowPreview(true);
    setGenerating(false);
    toast.success(`${Math.min(filtered.length, maxWords).toLocaleString()} mots generes a partir de ${credentials.length} credentials!`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-brand-black-light border border-orange-500/30 rounded-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-900/50 to-orange-800/30 px-6 py-4 border-b border-orange-500/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <BookOpen className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Generateur de Dictionnaire</h2>
              <p className="text-sm text-orange-400/70">Style CUPP - Mots de passe personnalisés</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-orange-500/20 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Extracted Data Banner */}
          {extractedData && extractedData.knownPasswords.length > 0 && (
            <div className="mb-6 bg-gradient-to-r from-green-900/30 to-green-800/20 rounded-xl border border-green-500/30 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <Database className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h4 className="text-green-400 font-semibold">Données extraites automatiquement</h4>
                    <p className="text-sm text-green-400/70">
                      {extractedData.knownPasswords.length} mot(s) de passe connu(s) •
                      {extractedData.phones.length > 0 && ` ${extractedData.phones.length} téléphone(s) •`}
                      {extractedData.usernames.length > 0 && ` ${extractedData.usernames.length} username(s) •`}
                      {extractedData.names.length > 0 && ` ${extractedData.names.length} nom(s)`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">
                    {extractedData.totalCredentials} credential(s) analysé(s)
                  </span>
                </div>
              </div>
              {/* Show known passwords preview */}
              {extractedData.knownPasswords.length > 0 && (
                <div className="mt-3 pt-3 border-t border-green-500/20">
                  <div className="text-xs text-green-400/70 mb-2">Mots de passe connus (utilisés comme base) :</div>
                  <div className="flex flex-wrap gap-2">
                    {extractedData.knownPasswords.slice(0, 8).map((pwd, i) => (
                      <span key={i} className="px-2 py-1 bg-green-500/10 text-green-300 rounded font-mono text-xs">
                        {pwd.length > 12 ? pwd.substring(0, 12) + '...' : pwd}
                      </span>
                    ))}
                    {extractedData.knownPasswords.length > 8 && (
                      <span className="text-xs text-green-400/50">+{extractedData.knownPasswords.length - 8} autres</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Workspace Selection & Generation Mode */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Workspace Selector */}
            <div className="bg-brand-black/50 rounded-xl border border-blue-500/30 p-4">
              <label className="text-sm text-blue-400 font-medium mb-2 flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Workspace (optionnel)
              </label>
              <select
                value={selectedWorkspaceId}
                onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-blue-500/50"
                disabled={loadingWorkspaces}
              >
                <option value="">-- Aucun workspace --</option>
                {workspaces.map(ws => (
                  <option key={ws.id} value={ws.id}>
                    {ws.name || ws.company_name || 'Workspace'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Associer le dictionnaire a un workspace client
              </p>
            </div>

            {/* Batch Mode - Use All Credentials */}
            <div className="bg-brand-black/50 rounded-xl border border-purple-500/30 p-4">
              <label className="text-sm text-purple-400 font-medium mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Mode de génération
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useAllCredentials}
                    onChange={(e) => setUseAllCredentials(e.target.checked)}
                    className="rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                  />
                  <span className="text-sm text-white">Utiliser TOUS les credentials</span>
                </label>
                <p className="text-xs text-gray-500">
                  {useAllCredentials
                    ? `Genere a partir de ${credentials?.length || 0} credentials`
                    : 'Genere pour un seul email/compte'}
                </p>
              </div>
              {useAllCredentials && (
                <button
                  type="button"
                  onClick={generateFromAllCredentials}
                  disabled={generating || !credentials?.length}
                  className="mt-3 w-full px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  Générer depuis tous
                </button>
              )}
            </div>

            {/* Export Format */}
            <div className="bg-brand-black/50 rounded-xl border border-cyan-500/30 p-4">
              <label className="text-sm text-cyan-400 font-medium mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Format d'export
              </label>
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-cyan-500/50"
              >
                {EXPORT_FORMATS.map(format => (
                  <option key={format.value} value={format.value}>
                    {format.icon} {format.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                {EXPORT_FORMATS.find(f => f.value === exportFormat)?.description}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Profile Form */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <User className="w-5 h-5 text-orange-400" />
                Informations Cible
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Prénom</label>
                  <input
                    type="text"
                    value={profile.firstName}
                    onChange={(e) => setProfile({...profile, firstName: e.target.value})}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500/50"
                    placeholder="Jean"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Nom</label>
                  <input
                    type="text"
                    value={profile.lastName}
                    onChange={(e) => setProfile({...profile, lastName: e.target.value})}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500/50"
                    placeholder="Dupont"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Surnom</label>
                  <input
                    type="text"
                    value={profile.nickname}
                    onChange={(e) => setProfile({...profile, nickname: e.target.value})}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500/50"
                    placeholder="jojo"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Date de naissance</label>
                  <input
                    type="text"
                    value={profile.birthDate}
                    onChange={(e) => setProfile({...profile, birthDate: e.target.value})}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500/50"
                    placeholder="15/03/1985"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block flex items-center gap-1">
                    <Heart className="w-3 h-3" /> Partenaire
                  </label>
                  <input
                    type="text"
                    value={profile.partnerName}
                    onChange={(e) => setProfile({...profile, partnerName: e.target.value})}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500/50"
                    placeholder="Marie"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Enfant / Animal</label>
                  <input
                    type="text"
                    value={profile.childName}
                    onChange={(e) => setProfile({...profile, childName: e.target.value})}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500/50"
                    placeholder="Lucas"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block flex items-center gap-1">
                    <Building className="w-3 h-3" /> Entreprise
                  </label>
                  <input
                    type="text"
                    value={profile.company}
                    onChange={(e) => setProfile({...profile, company: e.target.value})}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500/50"
                    placeholder="acme"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Ville
                  </label>
                  <input
                    type="text"
                    value={profile.city}
                    onChange={(e) => setProfile({...profile, city: e.target.value})}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500/50"
                    placeholder="Paris"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Mots-clés (séparés par virgule)</label>
                <input
                  type="text"
                  value={profile.keywords}
                  onChange={(e) => setProfile({...profile, keywords: e.target.value})}
                  className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500/50"
                  placeholder="football, 2024, secret"
                />
              </div>

              {/* Word Count Selector */}
              <div className="bg-brand-black rounded-lg p-4 border border-gray-800 mb-4">
                <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                  <Hash className="w-4 h-4 text-orange-400" />
                  Nombre de mots à générer
                </h4>
                <div className="grid grid-cols-5 gap-2">
                  {WORD_COUNT_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setMaxWords(option.value)}
                      className={`p-2 rounded-lg border transition-all text-center ${
                        maxWords === option.value
                          ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                          : 'bg-brand-black-light border-gray-700 text-gray-400 hover:border-orange-500/50'
                      }`}
                    >
                      <div className="text-lg font-bold">{option.value.toLocaleString()}</div>
                      <div className="text-xs opacity-70">{option.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                <h4 className="text-sm font-semibold text-gray-300 mb-3">Options de génération</h4>

                {/* Known passwords options */}
                {profile.knownPasswords && profile.knownPasswords.length > 0 && (
                  <div className="mb-3 pb-3 border-b border-gray-700">
                    <div className="text-xs text-green-400 mb-2 flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Options mots de passe connus ({profile.knownPasswords.length})
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={profile.useKnownPasswords}
                          onChange={(e) => setProfile({...profile, useKnownPasswords: e.target.checked})}
                          className="accent-green-500"
                        />
                        Utiliser les mdp connus
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={profile.generateVariations}
                          onChange={(e) => setProfile({...profile, generateVariations: e.target.checked})}
                          className="accent-green-500"
                        />
                        Générer variations
                      </label>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={profile.addNumbers}
                      onChange={(e) => setProfile({...profile, addNumbers: e.target.checked})}
                      className="accent-orange-500"
                    />
                    Ajouter chiffres
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={profile.addSpecialChars}
                      onChange={(e) => setProfile({...profile, addSpecialChars: e.target.checked})}
                      className="accent-orange-500"
                    />
                    Caractères spéciaux
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={profile.leetSpeak}
                      onChange={(e) => setProfile({...profile, leetSpeak: e.target.checked})}
                      className="accent-orange-500"
                    />
                    Leet speak (4=a, 3=e)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={profile.addCommonPatterns}
                      onChange={(e) => setProfile({...profile, addCommonPatterns: e.target.checked})}
                      className="accent-orange-500"
                    />
                    Patterns courants
                  </label>
                </div>
                <div className="flex gap-4 mt-3">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500">Min longueur</label>
                    <input
                      type="number"
                      value={profile.minLength}
                      onChange={(e) => setProfile({...profile, minLength: parseInt(e.target.value) || 6})}
                      className="w-full px-2 py-1 bg-brand-black-light border border-gray-700 rounded text-white text-sm"
                      min="4"
                      max="30"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-gray-500">Max longueur</label>
                    <input
                      type="number"
                      value={profile.maxLength}
                      onChange={(e) => setProfile({...profile, maxLength: parseInt(e.target.value) || 20})}
                      className="w-full px-2 py-1 bg-brand-black-light border border-gray-700 rounded text-white text-sm"
                      min="4"
                      max="30"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={generateDictionary}
                disabled={generating}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-lg hover:shadow-lg hover:shadow-orange-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Génération...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Générer le dictionnaire
                  </>
                )}
              </button>
            </div>

            {/* Right: Preview */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-400" />
                  Apercu ({generatedWords.length} mots)
                </h3>
                <div className="flex items-center gap-2">
                  {/* Load saved dictionaries button */}
                  <button
                    onClick={loadDictionaries}
                    disabled={loadingDictionaries}
                    className="px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors flex items-center gap-2 text-sm"
                  >
                    {loadingDictionaries ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FolderOpen className="w-4 h-4" />
                    )}
                    Charger
                  </button>

                  {generatedWords.length > 0 && (
                    <>
                      {/* Save dictionary button */}
                      <button
                        onClick={() => setShowSaveModal(true)}
                        className="px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors flex items-center gap-2 text-sm"
                      >
                        <Save className="w-4 h-4" />
                        Sauvegarder
                      </button>

                      {/* Download button */}
                      <button
                        onClick={downloadDictionary}
                        className="px-3 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors flex items-center gap-2 text-sm"
                      >
                        <Download className="w-4 h-4" />
                        Telecharger
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-brand-black rounded-lg border border-gray-800 h-[400px] overflow-hidden">
                {generatedWords.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Remplissez les informations et cliquez sur Générer</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full overflow-y-auto p-4 font-mono text-sm">
                    {generatedWords.slice(0, 500).map((word, i) => (
                      <div key={i} className="text-gray-300 py-0.5 hover:bg-orange-500/10 px-2 rounded flex items-center justify-between group">
                        <span>{word}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(word);
                            toast.success('Copie!');
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-orange-500/20 rounded"
                        >
                          <Copy className="w-3 h-3 text-orange-400" />
                        </button>
                      </div>
                    ))}
                    {generatedWords.length > 500 && (
                      <div className="text-center text-gray-500 py-4">
                        ... et {generatedWords.length - 500} autres mots
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              {generatedWords.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-brand-black rounded-lg p-3 border border-gray-800 text-center">
                    <div className="text-2xl font-bold text-orange-400">{generatedWords.length}</div>
                    <div className="text-xs text-gray-500">Total mots</div>
                  </div>
                  <div className="bg-brand-black rounded-lg p-3 border border-gray-800 text-center">
                    <div className="text-2xl font-bold text-blue-400">
                      {Math.round(generatedWords.reduce((a, b) => a + b.length, 0) / generatedWords.length)}
                    </div>
                    <div className="text-xs text-gray-500">Longueur moy.</div>
                  </div>
                  <div className="bg-brand-black rounded-lg p-3 border border-gray-800 text-center">
                    <div className="text-2xl font-bold text-green-400">
                      {Math.round(generatedWords.join('\n').length / 1024)} KB
                    </div>
                    <div className="text-xs text-gray-500">Taille fichier</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save Dictionary Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-brand-black-light border border-purple-500/30 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Save className="w-5 h-5 text-purple-400" />
                Sauvegarder le dictionnaire
              </h3>
              <button onClick={() => setShowSaveModal(false)} className="p-1 hover:bg-gray-700 rounded">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Nom du dictionnaire *</label>
                <input
                  type="text"
                  value={dictionaryName}
                  onChange={(e) => setDictionaryName(e.target.value)}
                  placeholder="Ex: Dict_ACME_JeanDupont"
                  className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500/50"
                  autoFocus
                />
              </div>

              <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Mots:</span>
                    <span className="text-white ml-2 font-medium">{generatedWords.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Taille:</span>
                    <span className="text-white ml-2 font-medium">{Math.round(generatedWords.join('\n').length / 1024)} KB</span>
                  </div>
                  {selectedEmail && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Cible:</span>
                      <span className="text-purple-400 ml-2 font-mono text-xs">{selectedEmail}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={saveDictionary}
                  disabled={saving || !dictionaryName.trim()}
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Load Dictionaries Modal */}
      {showDictionariesList && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-brand-black-light border border-blue-500/30 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-blue-500/30">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Library className="w-5 h-5 text-blue-400" />
                Dictionnaires sauvegardes ({savedDictionaries.length})
              </h3>
              <button onClick={() => setShowDictionariesList(false)} className="p-1 hover:bg-gray-700 rounded">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {savedDictionaries.length === 0 ? (
                <div className="text-center py-12">
                  <Library className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-500">Aucun dictionnaire sauvegarde</p>
                  <p className="text-sm text-gray-600 mt-2">Generez et sauvegardez un dictionnaire pour le retrouver ici</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedDictionaries.map((dict) => (
                    <div
                      key={dict.id}
                      className="bg-brand-black rounded-lg border border-gray-800 p-4 hover:border-blue-500/50 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-500/20 rounded-lg">
                              <BookOpen className="w-5 h-5 text-blue-400" />
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-white font-medium truncate">{dict.name}</h4>
                              {dict.description && (
                                <p className="text-xs text-gray-500 truncate">{dict.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              {dict.words_count?.toLocaleString()} mots
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {dict.file_size_kb?.toFixed(1)} KB
                            </span>
                            {dict.target_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {dict.target_email}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(dict.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => exportSavedDictionary(dict.id, dict.name)}
                            className="px-3 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors flex items-center gap-1 text-sm"
                            title="Telecharger .txt"
                          >
                            <Download className="w-4 h-4" />
                            Export
                          </button>
                          <button
                            onClick={() => loadDictionary(dict.id)}
                            className="px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors flex items-center gap-1 text-sm"
                            title="Charger pour modification"
                          >
                            <FolderOpen className="w-4 h-4" />
                            Charger
                          </button>
                          <button
                            onClick={() => deleteDictionary(dict.id)}
                            className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title={t('common.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const CredentialsList = () => {
  usePageTitle('Dark Web Credentials - SYLINK');
  const { t } = useI18n();
  const { workspaceId: activeWorkspaceId } = useWorkspace();
  const { hasFeature, currentPlan, isFreePlan } = usePlanRestrictions();

  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchDomain, setSearchDomain] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [showCGUModal, setShowCGUModal] = useState(false);
  const [selectedCredentials, setSelectedCredentials] = useState([]);
  const [showPasswords, setShowPasswords] = useState({});
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grouped'
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showDictGenerator, setShowDictGenerator] = useState(false);
  const [selectedEmailForDict, setSelectedEmailForDict] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    with_passwords: 0,
    tested: 0,
    successful: 0,
    unique_domains: 0,
    breaches_count: 0
  });

  // Slide panel for credential details
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [showCredentialPanel, setShowCredentialPanel] = useState(false);

  const handleSearchClick = () => {
    if (!searchDomain.trim()) {
      toast.error('Veuillez entrer un domaine email (ex: @exemple.fr)');
      return;
    }
    setShowCGUModal(true);
  };

  const handleCGUAccept = async () => {
    await handleSearchDomain();
  };

  // Blocked domains
  const BLOCKED_DOMAINS = ['sylink.fr', 'sylink.com', 'sylink.eu', 'sylink.io'];

  const handleSearchDomain = async () => {
    setSearching(true);
    try {
      const domain = searchDomain.replace('@', '').trim().toLowerCase();

      // Check blocked domains
      if (BLOCKED_DOMAINS.some(blocked => domain === blocked || domain.endsWith('.' + blocked))) {
        toast.error('La recherche sur les domaines SYLink n\'est pas autorisée');
        setSearching(false);
        return;
      }

      // Use API client for proper 401 handling — pass workspace_id to tag results
      const data = await credentialsApi.searchDomain(domain, activeWorkspaceId);
      setRecentSearches(prev => {
        const updated = [domain, ...prev.filter(d => d !== domain)].slice(0, 5);
        localStorage.setItem('recentDomainSearches', JSON.stringify(updated));
        return updated;
      });
      toast.success(`${data.credentials_found || 0} credentials trouvés pour @${domain}`);
      // Fetch immediately — backend has already committed before returning
      await fetchCredentials();
      await fetchStats();
    } catch (error) {
      // Silently handle 401 - user will be redirected to login by API interceptor
      if (error.response?.status !== 401) {
        console.error('Search error:', error);
        toast.error('Erreur: ' + (error.response?.data?.detail || error.message || 'Recherche échouée'));
      }
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    // Reset immédiatement au changement de workspace
    setCredentials([]);
    setLoading(true);
    fetchCredentials();
    fetchStats();
    const saved = localStorage.getItem('recentDomainSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, [activeWorkspaceId]);

  const fetchCredentials = async () => {
    try {
      // Use API client for proper 401 handling
      const data = await credentialsApi.getAll(activeWorkspaceId);
      setCredentials(data || []);
    } catch (error) {
      // Silently handle 401 - user will be redirected to login by API interceptor
      if (error.response?.status !== 401) {
        console.error('Failed to fetch credentials:', error);
      }
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Use API client for proper 401 handling
      const data = await credentialsApi.getStats(activeWorkspaceId);
      setStats({
        total: data.total_credentials || 0,
        with_passwords: data.with_plain_passwords || 0,
        tested: data.tested || 0,
        successful: data.successful_authentications || 0,
        unique_domains: data.unique_domains || 0,
        breaches_count: data.breaches_count || 0
      });
    } catch (error) {
      // Silently handle 401 - user will be redirected to login by API interceptor
      if (error.response?.status !== 401) {
        console.error('Failed to fetch stats:', error);
      }
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchCredentials();
    await fetchStats();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleValidateForReport = async () => {
    if (selectedCredentials.length === 0) return;

    setIsValidating(true);
    try {
      const result = await credentialsApi.validateForReport(selectedCredentials, true);
      setValidationMessage({
        type: 'success',
        text: `${result.validated_count} credential(s) valide(s) pour les rapports`
      });
      toast.success(`${result.validated_count} credential(s) valide(s) pour les rapports`);
      setSelectedCredentials([]);
      setTimeout(() => setValidationMessage(null), 5000);
    } catch (error) {
      console.error('Validation error:', error);
      setValidationMessage({
        type: 'error',
        text: 'Erreur lors de la validation'
      });
      toast.error('Erreur lors de la validation');
    } finally {
      setIsValidating(false);
    }
  };

  // Export credentials to CSV
  const handleExportCSV = (credentialsToExport = null) => {
    const dataToExport = credentialsToExport ||
      (selectedCredentials.length > 0
        ? credentials.filter(c => selectedCredentials.includes(c.id))
        : credentials);

    if (dataToExport.length === 0) {
      toast.error('Aucun credential a exporter');
      return;
    }

    // CSV Header
    const headers = ['Email', t('credentials.username'), t('credentials.password'), t('credentials.hash'), t('credentials.source'), 'Breach', 'IP Address', 'Phone', t('credentials.foundAt'), 'Tested', 'Valid'];

    // CSV Rows
    const rows = dataToExport.map(cred => [
      cred.email || '',
      cred.username || '',
      cred.password || '',
      cred.password_hash || '',
      cred.source || '',
      cred.breach_name || '',
      cred.ip_address || '',
      cred.phone || '',
      cred.discovered_at || '',
      cred.tested ? 'Oui' : 'Non',
      cred.auth_success ? 'Oui' : 'Non'
    ]);

    // Build CSV content
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    // Add BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `credentials_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`${dataToExport.length} credential(s) exporte(s) en CSV`);
  };

  // Export credentials to JSON
  const handleExportJSON = (credentialsToExport = null) => {
    const dataToExport = credentialsToExport ||
      (selectedCredentials.length > 0
        ? credentials.filter(c => selectedCredentials.includes(c.id))
        : credentials);

    if (dataToExport.length === 0) {
      toast.error('Aucun credential a exporter');
      return;
    }

    // Clean data for export (remove internal fields)
    const cleanData = dataToExport.map(cred => ({
      email: cred.email,
      username: cred.username,
      password: cred.password,
      password_hash: cred.password_hash,
      source: cred.source,
      breach_name: cred.breach_name,
      ip_address: cred.ip_address,
      phone: cred.phone,
      discovered_at: cred.discovered_at,
      tested: cred.tested,
      auth_success: cred.auth_success
    }));

    const jsonContent = JSON.stringify(cleanData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `credentials_export_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`${dataToExport.length} credential(s) exporte(s) en JSON`);
  };

  // Export as password list only (for hashcat/john)
  const handleExportPasswordList = () => {
    const dataToExport = selectedCredentials.length > 0
      ? credentials.filter(c => selectedCredentials.includes(c.id))
      : credentials;

    const passwords = dataToExport
      .filter(c => c.password)
      .map(c => c.password)
      .filter((v, i, a) => a.indexOf(v) === i); // Unique

    if (passwords.length === 0) {
      toast.error('Aucun mot de passe a exporter');
      return;
    }

    const content = passwords.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `passwords_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`${passwords.length} mot(s) de passe unique(s) exporte(s)`);
  };

  // Export state for dropdown
  const [showExportMenu, setShowExportMenu] = useState(false);

  const togglePasswordVisibility = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copie!`);
  };

  const toggleSelectCredential = (id) => {
    setSelectedCredentials(prev =>
      prev.includes(id) ? prev.filter(cId => cId !== id) : [...prev, id]
    );
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const toggleGroupExpand = (email) => {
    setExpandedGroups(prev => ({ ...prev, [email]: !prev[email] }));
  };

  // Group credentials by email
  const groupedCredentials = useMemo(() => {
    const groups = {};
    credentials.forEach(cred => {
      const key = cred.email || cred.username || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          email: key,
          credentials: [],
          breaches: new Set(),
          hasPassword: false,
          hasHash: false,
          tested: false,
          successful: false,
          latestDate: null
        };
      }
      groups[key].credentials.push(cred);
      if (cred.breach_name) groups[key].breaches.add(cred.breach_name);
      if (cred.source) groups[key].breaches.add(cred.source);
      if (cred.password) groups[key].hasPassword = true;
      if (cred.password_hash && !cred.password) groups[key].hasHash = true;
      if (cred.tested) groups[key].tested = true;
      if (cred.auth_success) groups[key].successful = true;
      if (cred.discovered_at) {
        const date = new Date(cred.discovered_at);
        if (!groups[key].latestDate || date > groups[key].latestDate) {
          groups[key].latestDate = date;
        }
      }
    });
    return Object.values(groups).sort((a, b) => {
      if (sortBy === 'date') {
        return sortOrder === 'desc'
          ? (b.latestDate || 0) - (a.latestDate || 0)
          : (a.latestDate || 0) - (b.latestDate || 0);
      }
      if (sortBy === 'email') {
        return sortOrder === 'desc'
          ? b.email.localeCompare(a.email)
          : a.email.localeCompare(b.email);
      }
      return 0;
    });
  }, [credentials, sortBy, sortOrder]);

  // Filter credentials
  let filteredCredentials = credentials.filter(cred => {
    if (filter === 'with_password') return cred.password;
    if (filter === 'tested') return cred.tested;
    if (filter === 'successful') return cred.auth_success;
    if (filter === 'hashed') return cred.password_hash && !cred.password;
    return true;
  });

  // Search filter
  if (searchQuery) {
    filteredCredentials = filteredCredentials.filter(cred =>
      (cred.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (cred.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (cred.source || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (cred.breach_name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  // Sort
  filteredCredentials = [...filteredCredentials].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'date') {
      comparison = new Date(b.discovered_at || 0) - new Date(a.discovered_at || 0);
    } else if (sortBy === 'email') {
      comparison = (a.email || a.username || '').localeCompare(b.email || b.username || '');
    } else if (sortBy === 'source') {
      comparison = (a.source || '').localeCompare(b.source || '');
    }
    return sortOrder === 'asc' ? -comparison : comparison;
  });

  // Filter grouped credentials
  const filteredGroupedCredentials = useMemo(() => {
    if (!searchQuery) return groupedCredentials;
    return groupedCredentials.filter(group =>
      group.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      Array.from(group.breaches).some(b => b.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [groupedCredentials, searchQuery]);

  // Stats calculees
  const uniqueDomains = [...new Set(credentials.map(c => {
    const email = c.email || '';
    const match = email.match(/@(.+)$/);
    return match ? match[1] : null;
  }).filter(Boolean))];

  const uniqueEmails = [...new Set(credentials.map(c => c.email).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <CGUModal
        isOpen={showCGUModal}
        onClose={() => setShowCGUModal(false)}
        onAccept={handleCGUAccept}
        scanType="Recherche Dark Web / Credentials"
      />

      <DictionaryGeneratorModal
        isOpen={showDictGenerator}
        onClose={() => {
          setShowDictGenerator(false);
          setSelectedEmailForDict(null);
        }}
        credentials={credentials}
        selectedEmail={selectedEmailForDict}
      />

      <div className="min-h-screen bg-brand-black p-6">
        <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
          {/* Welcome Banner */}
          <CredentialsWelcomeBanner />

          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gradient-green flex items-center gap-3">
                <Key className="w-10 h-10 text-purple-400" />
                Dark Web Credentials
              </h1>
              <p className="text-gray-400 mt-2 leading-relaxed">Credentials compromis découverts via Dark Web OSINT</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowDictGenerator(true)}
                className="px-4 py-3 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-all flex items-center gap-2"
                title="Generateur de dictionnaire"
              >
                <BookOpen className="w-5 h-5" />
                <span>Dictionnaire</span>
              </button>
              <button
                onClick={handleRefresh}
                className={`p-3 bg-brand-black-light border border-gray-700 rounded-lg hover:border-purple-500/50 transition-all ${isRefreshing ? 'animate-spin' : ''}`}
                title="Rafraichir"
              >
                <RefreshCw className="w-5 h-5 text-gray-400" />
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="px-4 py-3 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-all flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  <span>Exporter</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-brand-black-light border border-gray-700 rounded-xl shadow-xl z-50 py-2">
                    <button
                      onClick={() => { handleExportCSV(); setShowExportMenu(false); }}
                      className="w-full px-4 py-2 text-left text-gray-300 hover:bg-purple-500/20 hover:text-white flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Export CSV (Excel)
                    </button>
                    <button
                      onClick={() => { handleExportJSON(); setShowExportMenu(false); }}
                      className="w-full px-4 py-2 text-left text-gray-300 hover:bg-purple-500/20 hover:text-white flex items-center gap-2"
                    >
                      <Database className="w-4 h-4" />
                      Export JSON
                    </button>
                    <button
                      onClick={() => { handleExportPasswordList(); setShowExportMenu(false); }}
                      className="w-full px-4 py-2 text-left text-gray-300 hover:bg-purple-500/20 hover:text-white flex items-center gap-2"
                    >
                      <Lock className="w-4 h-4" />
                      Liste mots de passe (.txt)
                    </button>
                    <div className="border-t border-gray-700 my-1"></div>
                    <p className="px-4 py-1 text-xs text-gray-500">
                      {selectedCredentials.length > 0
                        ? `${selectedCredentials.length} selectionne(s)`
                        : `Tous (${credentials.length})`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 overflow-x-auto">
            {/* Total Credentials */}
            <div className="bg-gradient-to-br from-purple-900/30 to-brand-black border border-purple-500/30 rounded-xl p-4 hover:border-purple-500/50 transition-all relative group cursor-help">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total</span>
                <Database className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-3xl font-bold text-purple-400">{stats.total}</div>
              <div className="text-xs text-purple-400/60 mt-1">{t('credentials.title')}</div>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-purple-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-purple-400" />
                  <span className="font-semibold text-white">Total Credentials</span>
                </div>
                <p className="text-xs text-gray-300">Nombre total de credentials collectés (emails, usernames, mots de passe, hashes) depuis les scans et recherches Dark Web.</p>
              </div>
            </div>

            {/* Unique Emails */}
            <div className="bg-gradient-to-br from-indigo-900/30 to-brand-black border border-indigo-500/30 rounded-xl p-4 hover:border-indigo-500/50 transition-all relative group cursor-help">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Emails</span>
                <Mail className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="text-3xl font-bold text-indigo-400">{uniqueEmails.length}</div>
              <div className="text-xs text-indigo-400/60 mt-1">Uniques</div>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-indigo-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="font-semibold text-white">Emails Uniques</span>
                </div>
                <p className="text-xs text-gray-300">Adresses email distinctes trouvées. Peuvent être utilisées pour des attaques de phishing ciblé ou des vérifications de fuites.</p>
              </div>
            </div>

            {/* Passwords in Clear */}
            <div className="bg-gradient-to-br from-red-900/30 to-brand-black border border-red-500/30 rounded-xl p-4 hover:border-red-500/50 transition-all relative group cursor-help">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">{t('credentials.password')}s</span>
                <Lock className="w-5 h-5 text-red-400" />
              </div>
              <div className="text-3xl font-bold text-red-400">{stats.with_passwords}</div>
              <div className="text-xs text-red-400/60 mt-1">En clair</div>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-red-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-red-400" />
                  <span className="font-semibold text-white">Mots de Passe en Clair</span>
                </div>
                <p className="text-xs text-gray-300">Credentials avec mot de passe dechiffre ou trouve en clair dans les fuites. Risque critique - a tester immediatement pour password reuse.</p>
              </div>
            </div>

            {/* Hashes */}
            <div className="bg-gradient-to-br from-yellow-900/30 to-brand-black border border-yellow-500/30 rounded-xl p-4 hover:border-yellow-500/50 transition-all relative group cursor-help">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">{t('credentials.hash')}es</span>
                <Hash className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="text-3xl font-bold text-yellow-400">{credentials.filter(c => c.password_hash && !c.password).length}</div>
              <div className="text-xs text-yellow-400/60 mt-1">A cracker</div>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-yellow-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="flex items-center gap-2 mb-2">
                  <Hash className="w-4 h-4 text-yellow-400" />
                  <span className="font-semibold text-white">Hashes a Cracker</span>
                </div>
                <p className="text-xs text-gray-300">Hashes de mots de passe non encore casses (MD5, SHA, NTLM, bcrypt...). Peuvent être soumis au module de cracking pour récupération.</p>
              </div>
            </div>

            {/* Tested */}
            <div className="bg-gradient-to-br from-blue-900/30 to-brand-black border border-blue-500/30 rounded-xl p-4 hover:border-blue-500/50 transition-all relative group cursor-help">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Testes</span>
                <Zap className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-3xl font-bold text-blue-400">{stats.tested}</div>
              <div className="text-xs text-blue-400/60 mt-1">{t('credentials.title')}</div>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-blue-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-blue-400" />
                  <span className="font-semibold text-white">Credentials Testes</span>
                </div>
                <p className="text-xs text-gray-300">Credentials qui ont ete testes contre les services cibles (SSH, RDP, Web login, AD...) pour verifier leur validite.</p>
              </div>
            </div>

            {/* Valid/Successful */}
            <div className="bg-gradient-to-br from-green-900/30 to-brand-black border border-green-500/30 rounded-xl p-4 hover:border-green-500/50 transition-all relative group cursor-help">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Valides</span>
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-3xl font-bold text-green-400">{stats.successful}</div>
              <div className="text-xs text-green-400/60 mt-1">Auth réussies</div>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-green-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="font-semibold text-white">Authentifications Réussies</span>
                </div>
                <p className="text-xs text-gray-300">Credentials valides confirmés. L'authentification a réussi - accès potentiel aux systèmes cibles. Risque élevé.</p>
              </div>
            </div>

            {/* Unique Domains */}
            <div className="bg-gradient-to-br from-cyan-900/30 to-brand-black border border-cyan-500/30 rounded-xl p-4 hover:border-cyan-500/50 transition-all relative group cursor-help">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">{t('credentials.domain')}s</span>
                <Globe className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-3xl font-bold text-cyan-400">{uniqueDomains.length}</div>
              <div className="text-xs text-cyan-400/60 mt-1">Uniques</div>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-cyan-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-cyan-400" />
                  <span className="font-semibold text-white">Domaines Uniques</span>
                </div>
                <p className="text-xs text-gray-300">Nombre de domaines differents concernes par les credentials. Permet d'identifier l'etendue de l'exposition des donnees.</p>
              </div>
            </div>
          </div>

          {/* Search Box */}
          <div className="bg-gradient-to-r from-purple-900/40 to-purple-800/20 rounded-xl border-2 border-purple-500/50 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Search className="w-6 h-6 text-purple-400" />
                <h2 className="text-xl font-bold text-white">Recherche Dark Web par Domaine</h2>
              </div>
              {recentSearches.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Recent:</span>
                  {recentSearches.slice(0, 3).map((domain, i) => (
                    <button
                      key={i}
                      onClick={() => setSearchDomain(domain)}
                      className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded hover:bg-purple-500/30 transition-colors"
                    >
                      @{domain}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchDomain}
                  onChange={(e) => setSearchDomain(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchClick()}
                  placeholder="@exemple.fr ou exemple.fr"
                  className="w-full pl-12 pr-4 py-3 bg-brand-black border-2 border-purple-500/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <LicenseBlock feature="la recherche Dark Web">
              <PlanRestrictedButton
                feature="data_leak_search"
                onClick={handleSearchClick}
                disabled={searching}
                className="px-8 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold rounded-lg hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-50 flex items-center gap-2"
                disabledClassName="!bg-gray-700 !from-gray-700 !to-gray-700 !text-gray-400"
                tooltipPosition="bottom"
              >
                {searching ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Recherche...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Rechercher sur Darknet
                  </>
                )}
              </PlanRestrictedButton>
              </LicenseBlock>
            </div>
          </div>

          {/* OSINT Breach Checker Section */}
          <OSINTBreachChecker
            onResultsFound={(data) => {
              console.log('OSINT results:', data);
              // Optionally refresh credentials list
              // handleRefresh();
            }}
          />

          {/* OSINT Enrichment Panel - Scan credential enrichment by data type */}
          <OSINTEnrichmentPanel
            workspaceId={activeWorkspaceId}
            onEnrichmentComplete={(data) => {
              console.log('Enrichment complete:', data);
              // Refresh credentials if needed
            }}
          />

          {/* Toolbar */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-purple-500/30">
            <div className="flex items-center justify-between flex-wrap gap-4">
              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 bg-brand-black rounded-lg p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'list' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    Liste
                  </span>
                </button>
                <button
                  onClick={() => setViewMode('grouped')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'grouped' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <Layers className="w-4 h-4" />
                    Par Email
                  </span>
                </button>
              </div>

              {/* Search in results */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Filtrer par email, source, breach..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50"
                />
              </div>

              {/* Filters */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                    filter === 'all'
                      ? 'bg-purple-500 text-white'
                      : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-purple-500/50'
                  }`}
                >
                  Tous ({credentials.length})
                </button>
                <button
                  onClick={() => setFilter('with_password')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                    filter === 'with_password'
                      ? 'bg-red-500 text-white'
                      : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-red-500/50'
                  }`}
                >
                  {t('credentials.password')}s ({credentials.filter(c => c.password).length})
                </button>
                <button
                  onClick={() => setFilter('hashed')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                    filter === 'hashed'
                      ? 'bg-yellow-500 text-black'
                      : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-yellow-500/50'
                  }`}
                >
                  {t('credentials.hash')}es ({credentials.filter(c => c.password_hash && !c.password).length})
                </button>
                <button
                  onClick={() => setFilter('tested')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                    filter === 'tested'
                      ? 'bg-blue-500 text-white'
                      : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-blue-500/50'
                  }`}
                >
                  Testes ({credentials.filter(c => c.tested).length})
                </button>
                <button
                  onClick={() => setFilter('successful')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                    filter === 'successful'
                      ? 'bg-green-500 text-white'
                      : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-green-500/50'
                  }`}
                >
                  Valides ({credentials.filter(c => c.auth_success).length})
                </button>
              </div>

              {/* Sort */}
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-gray-300 text-sm focus:outline-none focus:border-purple-500/50"
              >
                <option value="date-desc">Plus recent</option>
                <option value="date-asc">Plus ancien</option>
                <option value="email-asc">Email A-Z</option>
                <option value="email-desc">Email Z-A</option>
                <option value="source-asc">Source A-Z</option>
              </select>
            </div>
          </div>

          {/* Content */}
          <div className="bg-brand-black-light rounded-xl border border-purple-500/20 overflow-hidden">
            {viewMode === 'grouped' ? (
              // Grouped by Email View
              <>
                {filteredGroupedCredentials.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 mx-auto mb-6 bg-purple-500/10 rounded-full flex items-center justify-center">
                      <Key className="w-12 h-12 text-purple-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-300 mb-4">{t('credentials.noCredentials')}</h3>
                    <p className="text-gray-500 mb-6 max-w-xl mx-auto">
                      Utilisez la recherche par domaine ci-dessus pour découvrir les credentials compromis.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-800/50">
                    {filteredGroupedCredentials.map((group, idx) => (
                      <div key={group.email} className="hover:bg-purple-500/5 transition-all">
                        {/* Group Header */}
                        <div
                          className="px-6 py-4 cursor-pointer flex items-center justify-between"
                          onClick={() => toggleGroupExpand(group.email)}
                        >
                          <div className="flex items-center gap-4">
                            <button className="p-1">
                              {expandedGroups[group.email] ? (
                                <ChevronUp className="w-5 h-5 text-purple-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-500" />
                              )}
                            </button>
                            <div className="p-2 bg-purple-500/20 rounded-lg">
                              <Mail className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                              <div className="font-mono text-white font-medium">{group.email}</div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">
                                  {group.credentials.length} fuite{group.credentials.length > 1 ? 's' : ''}
                                </span>
                                {group.hasPassword && (
                                  <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded flex items-center gap-1">
                                    <Lock className="w-3 h-3" /> {t('credentials.password')}
                                  </span>
                                )}
                                {group.hasHash && (
                                  <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded flex items-center gap-1">
                                    <Hash className="w-3 h-3" /> {t('credentials.hash')}
                                  </span>
                                )}
                                {group.successful && (
                                  <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" /> Valide
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm text-gray-400">{t('credentials.source')}s</div>
                              <div className="text-xs text-gray-500">{Array.from(group.breaches).slice(0, 2).join(', ')}{group.breaches.size > 2 ? '...' : ''}</div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEmailForDict(group.email);
                                setShowDictGenerator(true);
                              }}
                              className="p-2 bg-orange-500/20 hover:bg-orange-500/30 rounded-lg transition-colors"
                              title="Générer dictionnaire pour cet email"
                            >
                              <BookOpen className="w-4 h-4 text-orange-400" />
                            </button>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {expandedGroups[group.email] && (
                          <div className="px-6 pb-4 pl-16 space-y-2">
                            {group.credentials.map((cred, i) => (
                              <div key={cred.id || i} className="flex items-center justify-between bg-brand-black rounded-lg p-3 border border-gray-800">
                                <div className="flex items-center gap-4">
                                  <div className="text-sm">
                                    <span className="text-blue-400">Base de données compromise</span>
                                    {cred.breach_name && <span className="text-gray-500 ml-2">({cred.breach_name})</span>}
                                    {cred.hibp_breaches && cred.hibp_breaches.breach_names && (
                                      <span className="ml-2">
                                        {cred.hibp_breaches.breach_names.slice(0, 2).map((breach, idx) => (
                                          <span key={idx} className="px-1 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[10px] font-medium ml-1">
                                            {breach}
                                          </span>
                                        ))}
                                        {cred.hibp_breaches.breach_names.length > 2 && (
                                          <span className="text-purple-300 text-[10px] ml-1">+{cred.hibp_breaches.breach_names.length - 2}</span>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  {cred.password ? (
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-red-400 bg-red-500/10 px-2 py-1 rounded text-sm">
                                        {showPasswords[cred.id] ? cred.password : '••••••••'}
                                      </span>
                                      <button onClick={() => togglePasswordVisibility(cred.id)} className="p-1 hover:bg-gray-800 rounded">
                                        {showPasswords[cred.id] ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                                      </button>
                                      <button onClick={() => copyToClipboard(cred.password, 'Password')} className="p-1 hover:bg-gray-800 rounded">
                                        <Copy className="w-4 h-4 text-gray-500" />
                                      </button>
                                    </div>
                                  ) : cred.password_hash ? (
                                    <span className="font-mono text-yellow-400 text-xs bg-yellow-500/10 px-2 py-1 rounded">
                                      {cred.password_hash.substring(0, 20)}...
                                    </span>
                                  ) : (
                                    <span className="text-gray-500 text-sm">Pas de password</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {cred.discovered_at ? new Date(cred.discovered_at).toLocaleDateString('fr-FR') : '-'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              // List View (Original)
              <>
                {filteredCredentials.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 mx-auto mb-6 bg-purple-500/10 rounded-full flex items-center justify-center">
                      <Key className="w-12 h-12 text-purple-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-300 mb-4">
                      {searchQuery ? 'Aucun resultat' : t('credentials.noCredentials')}
                    </h3>
                    <p className="text-gray-500 mb-6 max-w-xl mx-auto">
                      {searchQuery
                        ? `Aucun credential ne correspond a "${searchQuery}"`
                        : 'Utilisez la recherche par domaine ci-dessus pour découvrir les credentials compromis.'
                      }
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-brand-black border-b border-gray-800 text-sm font-semibold text-gray-400">
                      <div className="col-span-1 flex items-center">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCredentials(filteredCredentials.map(c => c.id));
                            } else {
                              setSelectedCredentials([]);
                            }
                          }}
                          checked={selectedCredentials.length === filteredCredentials.length && filteredCredentials.length > 0}
                          className="w-4 h-4 accent-purple-500"
                        />
                      </div>
                      <div className="col-span-3 flex items-center gap-1 cursor-pointer hover:text-white" onClick={() => handleSort('email')}>
                        Email / {t('credentials.username')}
                        {sortBy === 'email' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </div>
                      <div className="col-span-2">{t('credentials.password')}</div>
                      <div className="col-span-2 flex items-center gap-1 cursor-pointer hover:text-white" onClick={() => handleSort('source')}>
                        {t('credentials.source')} / Breach
                        {sortBy === 'source' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </div>
                      <div className="col-span-1 text-center">Statut</div>
                      <div className="col-span-2 flex items-center gap-1 cursor-pointer hover:text-white" onClick={() => handleSort('date')}>
                        Date
                        {sortBy === 'date' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </div>
                      <div className="col-span-1 text-center">Actions</div>
                    </div>

                    {/* Table Body */}
                    {filteredCredentials.map((cred, index) => (
                      <div
                        key={cred.id || index}
                        onClick={() => { setSelectedCredential(cred); setShowCredentialPanel(true); }}
                        className={`grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-800/50 hover:bg-purple-500/5 transition-all items-center cursor-pointer ${
                          selectedCredentials.includes(cred.id) ? 'bg-purple-500/10' : ''
                        }`}
                      >
                        <div className="col-span-1" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedCredentials.includes(cred.id)}
                            onChange={() => toggleSelectCredential(cred.id)}
                            className="w-4 h-4 accent-purple-500"
                          />
                        </div>
                        <div className="col-span-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-500/20 rounded-lg">
                              <Mail className="w-4 h-4 text-purple-400" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-mono text-white truncate">{cred.email || cred.username || '-'}</div>
                              {cred.email && cred.username && cred.email !== cred.username && (
                                <div className="text-xs text-gray-500 truncate">User: {cred.username}</div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-2" onClick={(e) => e.stopPropagation()}>
                          {cred.password ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-red-400 bg-red-500/10 px-2 py-1 rounded text-sm truncate max-w-[150px]">
                                {showPasswords[cred.id] ? cred.password : '••••••••'}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); togglePasswordVisibility(cred.id); }}
                                className="p-1 hover:bg-gray-800 rounded"
                              >
                                {showPasswords[cred.id] ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(cred.password, 'Password'); }}
                                className="p-1 hover:bg-gray-800 rounded"
                              >
                                <Copy className="w-4 h-4 text-gray-500" />
                              </button>
                            </div>
                          ) : cred.password_hash ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-yellow-400 text-xs truncate max-w-[120px]" title={cred.password_hash}>
                                {cred.password_hash.substring(0, 16)}...
                              </span>
                              <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">hash</span>
                            </div>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </div>
                        <div className="col-span-2">
                          <div className="flex flex-col gap-1">
                            <span className="text-blue-400 text-sm">{cred.source || 'Darknet'}</span>
                            {cred.breach_name && (
                              <span className="text-xs text-gray-500 truncate">{cred.breach_name}</span>
                            )}
                            {cred.hibp_breaches && cred.hibp_breaches.breach_names && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {cred.hibp_breaches.breach_names.slice(0, 3).map((breach, idx) => (
                                  <span key={idx} className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[10px] font-medium">
                                    {breach}
                                  </span>
                                ))}
                                {cred.hibp_breaches.breach_names.length > 3 && (
                                  <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-300 rounded text-[10px]">
                                    +{cred.hibp_breaches.breach_names.length - 3}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-span-1 flex justify-center">
                          {cred.auth_success ? (
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Valide
                            </span>
                          ) : cred.tested ? (
                            <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              Invalide
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs font-medium flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Non teste
                            </span>
                          )}
                        </div>
                        <div className="col-span-2">
                          <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <Calendar className="w-4 h-4" />
                            {cred.discovered_at ? new Date(cred.discovered_at).toLocaleDateString('fr-FR') : '-'}
                          </div>
                        </div>
                        <div className="col-span-1 flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEmailForDict(cred.email);
                              setShowDictGenerator(true);
                            }}
                            className="p-2 hover:bg-orange-500/20 rounded-lg transition-colors"
                            title="Générer dictionnaire"
                          >
                            <BookOpen className="w-4 h-4 text-gray-400 hover:text-orange-400" />
                          </button>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 hover:bg-purple-500/20 rounded-lg transition-colors"
                            title="Tester le credential"
                          >
                            <Play className="w-4 h-4 text-gray-400 hover:text-purple-400" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(`${cred.email || cred.username}:${cred.password || cred.password_hash}`, 'Credential'); }}
                            className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors"
                            title="Copier"
                          >
                            <Copy className="w-4 h-4 text-gray-400 hover:text-blue-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          {/* Bulk Actions Bar */}
          {selectedCredentials.length > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-brand-black-light border border-purple-500/50 rounded-xl px-6 py-4 shadow-2xl flex items-center gap-6 z-50">
              <span className="text-white font-medium">{selectedCredentials.length} credential(s) selectionne(s)</span>
              <div className="h-6 w-px bg-gray-700" />
              <button
                onClick={handleValidateForReport}
                disabled={isValidating}
                className="flex items-center gap-2 bg-brand-green text-brand-black px-4 py-2 rounded-lg font-bold hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                {isValidating ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <BadgeCheck className="w-4 h-4" />
                )}
                <span>{t('common.validateForReport')}</span>
              </button>
              <button
                onClick={() => {
                  const selectedCreds = credentials.filter(c => selectedCredentials.includes(c.id));
                  const firstEmail = selectedCreds[0]?.email;
                  if (firstEmail) {
                    setSelectedEmailForDict(firstEmail);
                    setShowDictGenerator(true);
                  }
                }}
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                <span>Dictionnaire</span>
              </button>
              <button className="flex items-center gap-2 text-gray-300 hover:text-purple-400 transition-colors">
                <Play className="w-4 h-4" />
                <span>Tester</span>
              </button>
              <button
                onClick={() => handleExportCSV()}
                className="flex items-center gap-2 text-brand-green hover:text-green-400 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Exporter CSV</span>
              </button>
              <button className="flex items-center gap-2 text-gray-300 hover:text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" />
                <span>{t('common.delete')}</span>
              </button>
              <button
                onClick={() => setSelectedCredentials([])}
                className="text-gray-500 hover:text-white transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          )}

          {/* Domains Summary */}
          {uniqueDomains.length > 0 && (
            <div className="bg-brand-black-light rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-cyan-400" />
                Domaines compromis ({uniqueDomains.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {uniqueDomains.map((domain, i) => {
                  const count = credentials.filter(c => (c.email || '').endsWith('@' + domain)).length;
                  return (
                    <div
                      key={i}
                      className="px-3 py-2 bg-brand-black border border-gray-700 rounded-lg flex items-center gap-2 hover:border-cyan-500/50 transition-colors cursor-pointer"
                      onClick={() => setSearchQuery('@' + domain)}
                    >
                      <span className="text-gray-300">@{domain}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Credential Details Slide Panel */}
      {showCredentialPanel && selectedCredential && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCredentialPanel(false)}
          />

          {/* Slide Panel */}
          <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-brand-black-light border-l border-purple-500/30 shadow-2xl overflow-y-auto transition-transform duration-300 ease-out">
            {/* Header */}
            <div className="sticky top-0 bg-brand-black-light border-b border-gray-800 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Shield className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Détails du Credential</h2>
                  <p className="text-sm text-gray-400">{selectedCredential.email || selectedCredential.username}</p>
                </div>
              </div>
              <button
                onClick={() => setShowCredentialPanel(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Identity Section */}
              <div className="bg-brand-black rounded-xl p-5 border border-gray-800">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-400" />
                  Identité
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Email</label>
                    <div className="mt-1 font-mono text-white flex items-center gap-2">
                      {selectedCredential.email || '-'}
                      {selectedCredential.email && (
                        <button onClick={() => copyToClipboard(selectedCredential.email, 'Email')} className="p-1 hover:bg-gray-800 rounded">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Username</label>
                    <div className="mt-1 font-mono text-white">{selectedCredential.username || '-'}</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Nom</label>
                    <div className="mt-1 text-white">{selectedCredential.name || '-'}</div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Domaine</label>
                    <div className="mt-1 text-cyan-400">{selectedCredential.domain || '-'}</div>
                  </div>
                </div>
              </div>

              {/* Credentials Section */}
              <div className="bg-brand-black rounded-xl p-5 border border-red-500/30">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5 text-red-400" />
                  Credentials
                </h3>
                <div className="space-y-4">
                  {selectedCredential.password && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Password (Clair)</label>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="font-mono text-red-400 bg-red-500/10 px-3 py-2 rounded-lg">
                          {showPasswords[selectedCredential.id] ? selectedCredential.password : '••••••••••••'}
                        </span>
                        <button
                          onClick={() => togglePasswordVisibility(selectedCredential.id)}
                          className="p-2 hover:bg-gray-800 rounded-lg"
                        >
                          {showPasswords[selectedCredential.id] ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                        </button>
                        <button
                          onClick={() => copyToClipboard(selectedCredential.password, 'Password')}
                          className="p-2 hover:bg-gray-800 rounded-lg"
                        >
                          <Copy className="w-5 h-5 text-gray-400" />
                        </button>
                      </div>
                    </div>
                  )}
                  {selectedCredential.password_hash && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Password Hash</label>
                      <div className="mt-1">
                        <div className="font-mono text-yellow-400 bg-yellow-500/10 px-3 py-2 rounded-lg text-sm break-all">
                          {selectedCredential.password_hash}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          {selectedCredential.hash_type && (
                            <span className="inline-block text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">
                              Type: {selectedCredential.hash_type}
                            </span>
                          )}
                          {!selectedCredential.password && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const response = await credentialsApi.crackHash(selectedCredential.id);
                                  if (response.success) {
                                    toast.success(`Hash décrypté: ${response.password}`);
                                    // Refresh credentials to show the cracked password
                                    fetchCredentials();
                                    setShowCredentialPanel(false);
                                  } else {
                                    toast.error(response.message || 'Impossible de décrypter ce hash');
                                  }
                                } catch (error) {
                                  toast.error('Erreur lors du décryptage');
                                }
                              }}
                              className="inline-flex items-center gap-1 text-xs px-3 py-1 bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-400 rounded hover:from-orange-500/30 hover:to-red-500/30 transition-all"
                            >
                              <Zap className="w-3 h-3" />
                              Décrypter
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {!selectedCredential.password && !selectedCredential.password_hash && (
                    <p className="text-gray-500 italic">Aucun mot de passe disponible</p>
                  )}
                </div>
              </div>

              {/* Contact Info Section */}
              {(selectedCredential.phone || selectedCredential.ip_address) && (
                <div className="bg-brand-black rounded-xl p-5 border border-gray-800">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-green-400" />
                    Informations de Contact
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedCredential.phone && (
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wide">Téléphone</label>
                        <div className="mt-1 text-white font-mono">{selectedCredential.phone}</div>
                      </div>
                    )}
                    {selectedCredential.ip_address && (
                      <div>
                        <label className="text-xs text-gray-500 uppercase tracking-wide">Adresse IP</label>
                        <div className="mt-1 text-white font-mono">{selectedCredential.ip_address}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Source & Breach Section */}
              <div className="bg-brand-black rounded-xl p-5 border border-blue-500/30">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-400" />
                  Source & Breach
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Source</label>
                      <div className="mt-1">
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg font-medium">
                          Base de données compromise
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Breach Name</label>
                      <div className="mt-1 text-white">{selectedCredential.breach_name || '-'}</div>
                    </div>
                  </div>

                  {/* HIBP Breaches */}
                  {selectedCredential.hibp_breaches && selectedCredential.hibp_breaches.breach_names && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[10px]">HIBP</span>
                        Breaches HaveIBeenPwned ({selectedCredential.hibp_breaches.breach_count})
                      </label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedCredential.hibp_breaches.breach_names.map((breach, idx) => (
                          <span key={idx} className="px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-sm font-medium">
                            {breach}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Metadata Section */}
              <div className="bg-brand-black rounded-xl p-5 border border-gray-800">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-400" />
                  Métadonnées
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Découvert le</label>
                    <div className="mt-1 text-white">
                      {selectedCredential.discovered_at
                        ? new Date(selectedCredential.discovered_at).toLocaleString('fr-FR')
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Type</label>
                    <div className="mt-1">
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedCredential.type === 'leaked_credential'
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {selectedCredential.type === 'leaked_credential' ? 'Dark Web' : 'Scan'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Testé</label>
                    <div className="mt-1">
                      {selectedCredential.tested ? (
                        <span className="text-green-400">✓ Oui</span>
                      ) : (
                        <span className="text-gray-500">Non</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wide">ID</label>
                    <div className="mt-1 font-mono text-xs text-gray-500">{selectedCredential.id}</div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-800">
                <button
                  onClick={() => {
                    setSelectedEmailForDict(selectedCredential.email);
                    setShowDictGenerator(true);
                    setShowCredentialPanel(false);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 px-4 py-3 rounded-lg font-medium transition-colors"
                >
                  <BookOpen className="w-5 h-5" />
                  Générer Dictionnaire
                </button>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(selectedCredential, null, 2), 'Credential data')}
                  className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 px-4 py-3 rounded-lg text-white transition-colors"
                >
                  <Copy className="w-5 h-5" />
                  Copier JSON
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CredentialsList;
